import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import * as fromRoot from '@core/redux';
import * as fromWiki from '@core/redux/wiki/wiki.reducer';
import { ApiWikiService } from '@core/services/entities/wiki/api-wiki.service';
import { EnsureLoadedSuccess } from '@core/redux/wiki/wiki.actions';
import { WikiModel } from '@core/models';
import { createSelector } from '@ngrx/store';

const getWikiNameById = (id: string) => createSelector(
  fromWiki.getWikiState,
  (state: fromWiki.State): string | null => {
    const entity = state.entities[id];
    return entity ? entity.name : null;
  },
);

export interface ParsedInternalLink {
  id: string;
  companyId: string | null;
}

@Injectable({ providedIn: 'root' })
export class WikiLinkResolverService {
  private readonly nameCache = new Map<string, Observable<string | null>>();
  private currentCompanyId: string | null = null;

  constructor(
    private readonly store: Store<fromRoot.State>,
    private readonly apiWikiService: ApiWikiService,
  ) {
    this.store
      .pipe(select(fromRoot.getCompanyCurrentId))
      .subscribe((companyId) => (this.currentCompanyId = companyId || null));
  }

  /**
   * Парсит URL и возвращает id статьи, если ссылка ведёт на запись wiki
   * текущей компании. Поддерживает абсолютные и относительные URL,
   * пути `/wiki`, `/wiki/active`, `/wiki/archive` и т.п.
   */
  parseInternalUrl(url: string): ParsedInternalLink | null {
    if (!url || typeof url !== 'string') {
      return null;
    }

    const trimmed = url.trim();
    if (!trimmed) {
      return null;
    }

    let parsed: URL;
    try {
      parsed = trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('//')
        ? new URL(trimmed, 'https://placeholder.local')
        : new URL(trimmed, 'https://placeholder.local/');
    } catch {
      return null;
    }

    const id = parsed.searchParams.get('id');
    if (!id) {
      return null;
    }

    const segments = parsed.pathname.split('/').filter(Boolean);
    const wikiIndex = segments.indexOf('wiki');
    if (wikiIndex === -1) {
      return null;
    }

    const companyId = wikiIndex > 0 ? segments[wikiIndex - 1] : null;
    if (companyId && this.currentCompanyId && companyId !== this.currentCompanyId) {
      return null;
    }

    return { id, companyId };
  }

  /**
   * Возвращает href, который мы кладём в `<a>` для внутренней ссылки.
   * Соответствует формату из `WikiService.getLink`, чтобы
   * `RouterTransformerDirective` корректно перехватил клик.
   */
  buildHref(id: string): string {
    const companyId = this.currentCompanyId || '';
    return `/${companyId}/wiki/active?id=${id}`;
  }

  /**
   * Поток актуального имени статьи. Сначала смотрим в стор;
   * если там нет — лениво подгружаем через API и публикуем в стор
   * через ViewActionSuccess. Кэш на уровне сервиса не даёт
   * параллельных запросов на один и тот же id.
   */
  resolveName(id: string): Observable<string | null> {
    if (!id) {
      return of(null);
    }

    if (!this.nameCache.has(id)) {
      const fromStore$ = this.store.pipe(select(getWikiNameById(id)));

      const lazyLoad$ = fromStore$.pipe(
        take(1),
        switchMap((name) => {
          if (name) {
            return of(name);
          }
          return this.apiWikiService.view(id).pipe(
            tap((wiki: WikiModel) => this.store.dispatch(new EnsureLoadedSuccess(wiki))),
            map((wiki: WikiModel) => (wiki ? wiki.name : null)),
          );
        }),
      );

      const stream$ = lazyLoad$.pipe(
        switchMap(() => fromStore$),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

      this.nameCache.set(id, stream$);
    }

    return this.nameCache.get(id);
  }

  /**
   * Синхронный снимок имени из стора (для случая, когда нужно
   * избежать «мигания» текста ссылки при первичном рендере).
   */
  getCachedName(id: string): string | null {
    let name: string | null = null;
    this.store
      .pipe(select(getWikiNameById(id)), take(1))
      .subscribe((value) => (name = value));
    return name;
  }

  /**
   * HTML внутренней ссылки. Единственное место, где собирается разметка,
   * чтобы вставка из кнопки и нормализация после paste давали одинаковый результат.
   */
  buildLinkHtml(id: string, name: string): string {
    const safeId = this._escapeAttr(id || '');
    const safeHref = this._escapeAttr(this.buildHref(id));
    const safeName = this._escapeText(name || '');
    return `<a class="wiki-internal-link" data-wiki-id="${safeId}" href="${safeHref}">${safeName}</a>`;
  }

  private _escapeAttr(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private _escapeText(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
